import { useAcoes, useEstado } from "../estado.jsx";
import { MenuDeAcoes } from "./comuns.jsx";
import { ICONES, Icone, s } from "../visual.jsx";

/**
 * Em que escritório esta aba está.
 *
 * Fica encostado na marca, e não atrás de um clique, porque o escritório
 * corrente é COOKIE (`crm_workspace`): trocar de lado numa aba muda o banco de
 * destino das OUTRAS abas já abertas. Sem o nome à vista, a pessoa continua
 * olhando a mesa do escritório A e gravando no B — e nada na tela avisa.
 *
 * Com um escritório só é rótulo, não seletor: lista de um item é clique que não
 * decide nada. Com dois ou mais, trocar recarrega a mesa inteira (ver
 * `trocarEscritorio`), porque tudo que está na tela veio do banco de lá.
 */
/** A cor do setor vem como NOME de token; a tela só a transforma em `var()`. */
export const corDoSetor = (cor) => (cor ? `var(--color-${cor})` : "var(--color-tinta-fantasma)");

/* O ponto colorido ao lado do nome: discreto de propósito — é para quem olha a
   marca saber EM QUE SETOR está antes de gravar, não um enfeite da casca. */
const Ponto = ({ cor }) =>
  cor ? <span aria-hidden="true" className="size-2 flex-none rounded-full" style={s(`background:${corDoSetor(cor)}`)} /> : null;

export default function Escritorio({ variante = "trilho" }) {
  const { workspace, workspaces } = useEstado();
  const { trocarEscritorio } = useAcoes();

  if (!workspace) return null;
  const nome = workspace.nome ?? workspace.chave;
  const varios = (workspaces ?? []).length > 1;
  const noTrilho = variante === "trilho";

  /* No trilho o nome tem 62px para caber, então quebra em duas linhas; na barra
     de cima a faixa é larga e uma linha basta. Cortar com reticências em vez de
     encolher a fonte: "Galera Advocacia Filial" a 7px ninguém lê. */
  const corpo = noTrilho
    ? "max-width:62px;font-size:11px;line-height:1.2;text-align:center;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden"
    : "max-width:100%;font-size:11px;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis";
  const comum = `${corpo};font-weight:700;letter-spacing:.03em;color:color-mix(in oklab, var(--color-creme) 62%, transparent);text-transform:uppercase`;
  const moldura = noTrilho
    ? "display:flex;flex-direction:column;align-items:center;gap:2px;margin-bottom:14px;flex:none"
    : "display:flex;align-items:center;gap:4px;padding:0 6px;min-width:0";

  if (!varios)
    return (
      <div style={s(moldura)}>
        <Ponto cor={workspace.cor} />
        <span style={s(comum)}>{nome}</span>
      </div>
    );

  return (
    <MenuDeAcoes
      ancora={noTrilho ? "right start" : "bottom start"}
      largura="230px"
      legenda="TROCAR DE SETOR"
      rotulo={`Setor ${nome} — trocar de setor`}
      titulo="Trocar de setor (recarrega a mesa)"
      estilo={({ aberto }) =>
        `${moldura};border:none;background:${aberto ? "color-mix(in oklab, var(--color-creme) 14%, transparent)" : "transparent"};border-radius:10px;cursor:pointer;padding:${noTrilho ? "4px 3px" : "2px 6px"}`
      }
      itens={(workspaces ?? []).map((w) => ({
        chave: w.chave,
        rotulo: w.nome ?? w.chave,
        titulo: w.chave === workspace.chave ? "Este é o setor desta aba" : `Ir para ${w.nome ?? w.chave}`,
        /* O "você está aqui" vai na segunda linha, não num visto ao lado: com
           visto só no item corrente os outros rótulos ficavam desalinhados. */
        detalhe: w.chave === workspace.chave ? "setor desta aba" : undefined,
        aoTocar: () => trocarEscritorio(w.chave),
      }))}
    >
      <Ponto cor={workspace.cor} />
      <span style={s(comum)}>{nome}</span>
      <Icone de={ICONES.baixo} tamanho={12} largura={2} cor="color-mix(in oklab, var(--color-creme) 50%, transparent)" />
    </MenuDeAcoes>
  );
}
